import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import RegistrationProgress from '../student-registration/components/RegistrationProgress';
import BenefitsPanel from '../student-registration/components/BenefitsPanel';
import CounselorPersonalInfoStep from './components/CounselorPersonalInfoStep';
import ProfessionalInfoStep from './components/ProfessionalInfoStep';
import InstitutionInfoStep from './components/InstitutionInfoStep';

const CounselorRegistration = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    region: '',
    licenseNumber: '',
    yearsExperience: '',
    specialization: [],
    certifications: '',
    institutionName: '',
    institutionType: '',
    position: '',
    studentCapacity: ''
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCheckboxChange = (fieldName, value, checked) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: checked
        ? [...prev?.[fieldName], value]
        : prev?.[fieldName]?.filter(item => item !== value)
    }));
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData?.firstName?.trim()) newErrors.firstName = 'Le prénom est requis';
      if (!formData?.lastName?.trim()) newErrors.lastName = 'Le nom est requis';
      if (!formData?.email?.trim()) {
        newErrors.email = 'L\'email est requis';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
        newErrors.email = 'Format d\'email invalide';
      }
      if (!formData?.phone?.trim()) newErrors.phone = 'Le numéro de téléphone est requis';
      if (!formData?.password) {
        newErrors.password = 'Le mot de passe est requis';
      } else if (formData?.password?.length < 6) {
        newErrors.password = 'Le mot de passe doit contenir au moins 6 caractères';
      }
      if (formData?.password !== formData?.confirmPassword) {
        newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
      }
      if (!formData?.region) newErrors.region = 'La région est requise';
    }

    if (step === 2) {
      if (!formData?.licenseNumber?.trim()) newErrors.licenseNumber = 'Le numéro de licence est requis';
      if (!formData?.yearsExperience) newErrors.yearsExperience = 'L\'expérience est requise';
      if (formData?.specialization?.length === 0) {
        newErrors.specialization = 'Sélectionnez au moins une spécialisation';
      }
    }

    if (step === 3) {
      if (!formData?.institutionName?.trim()) newErrors.institutionName = 'Le nom de l\'institution est requis';
      if (!formData?.institutionType) newErrors.institutionType = 'Le type d\'institution est requis';
      if (!formData?.position) newErrors.position = 'Le poste est requis';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) return;

    setIsSubmitting(true);

    try {
      const { data: authData, error: authError } = await supabase?.auth?.signUp({
        email: formData?.email,
        password: formData?.password,
        options: {
          data: {
            full_name: `${formData?.firstName} ${formData?.lastName}`,
            role: 'counselor'
          }
        }
      });

      if (authError) throw authError;

      console.log('Counselor registration successful:', authData);
      navigate('/counselor-management-portal');
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ submit: error?.message || 'Erreur lors de l\'inscription' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <CounselorPersonalInfoStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
          />
        );
      case 2:
        return (
          <ProfessionalInfoStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
            onCheckboxChange={handleCheckboxChange}
          />
        );
      case 3:
        return (
          <InstitutionInfoStep
            formData={formData}
            errors={errors}
            onChange={handleInputChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      <div className="content-area">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-10">
            <button
              onClick={() => navigate('/registration-role-selection')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 mb-4 md:mb-6"
            >
              <Icon name="ArrowLeft" size={20} />
              <span className="text-sm md:text-base">Retour</span>
            </button>

            <div className="text-center mb-6 md:mb-8 lg:mb-10">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-2 md:mb-3">
                Créez Votre Compte Conseiller
              </h1>
              <p className="text-sm md:text-base lg:text-lg text-gray-600">
                Accédez à des outils professionnels de gestion de cas étudiants en 3 étapes
              </p>
            </div>

            <RegistrationProgress currentStep={currentStep} totalSteps={3} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 lg:gap-10">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 lg:p-10">
                {renderStepContent()}

                {errors?.submit && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{errors?.submit}</p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 md:gap-4 mt-6 md:mt-8 lg:mt-10 pt-6 md:pt-8 border-t border-gray-200">
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      iconName="ChevronLeft"
                      iconPosition="left"
                      className="w-full sm:w-auto"
                    >
                      Précédent
                    </Button>
                  )}

                  {currentStep < 3 ? (
                    <Button
                      variant="default"
                      onClick={handleNext}
                      iconName="ChevronRight"
                      iconPosition="right"
                      className="w-full sm:flex-1"
                    >
                      Continuer
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      onClick={handleSubmit}
                      loading={isSubmitting}
                      iconName="Check"
                      iconPosition="right"
                      className="w-full sm:flex-1"
                    >
                      {isSubmitting ? 'Création en cours...' : 'Créer Mon Compte'}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <BenefitsPanel />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CounselorRegistration;